// getAccessoriesOfProductData

import {
  getObjectAtSystemDefinitionSlot,
  getSystemProjectSet,
  getComponentType,
  getProductsBySlotNameAndId,
} from '../project-definition-data-access';

import {
  AccessoryCategory,
  getSystemObjectAccessories as defGetSystemObjectAccessories,
  getProductInfoBySlotName as defGetProductInfoBySlotName,
  instantiate as defInstantiate,
  addAccessoryToArray as defAddAccessoryToArray,
  addOptionalAccessoryToArray as defAddOptionalAccessoryToArray,
  addAccessoryInstanceToArray as defAddAccessoryInstanceToArray,
} from './index';

function addRequiredAccessories(options, accessoryArray, type, systemDefinition, systemProducts) {
  if (!options || !accessoryArray) {
    return;
  }
  accessoryArray.forEach((acc) => {
    if (acc && acc.required) {
      defAddAccessoryToArray(type, acc, options, systemDefinition, systemProducts);
    }
  });
}

export function getIdOfAccessorySet(accessorySet) {
  return accessorySet ? accessorySet.set_entry_id : null;
}

export function getAccessoriesOfProductData(productData, category) {
  if (category === true || category === AccessoryCategory.ACCESSORY_COLLECTIONS) {
    return productData.accessory_collections || productData.optional_accessories;
  }
  return productData.accessories;
}

function isOptionalAccessoryInstance(accessoryInstance, product, slotName, systemDefinition, systemProducts) { // eslint-disable-line
  if (!accessoryInstance) {
    return false;
  }
  if (!product) {
    return false;
  }
  const accessoryCollections = getAccessoriesOfProductData(product, AccessoryCategory.ACCESSORY_COLLECTIONS);
  const accessoryCollectionsKeys = accessoryCollections ? Object.keys(accessoryCollections) : null;
  const numAccessoryCollectionsKeys = accessoryCollectionsKeys ? accessoryCollectionsKeys.length : 0;

  for (let j = 0; j < numAccessoryCollectionsKeys; j += 1) {
    const key = accessoryCollectionsKeys[j];
    const accessoryCollection = accessoryCollections[key];
    const accessoryCollectionSets = accessoryCollection ? accessoryCollection.sets : null;
    const numAccessoryCollectionSets = accessoryCollectionSets ? accessoryCollectionSets.length : 0;

    for (let i = 0; i < numAccessoryCollectionSets; i += 1) {
      const accSet = accessoryCollectionSets[i];
      if (accSet && getIdOfAccessorySet(accSet) === accessoryInstance.set) {
        return true;
      }
    }
  }

  return false;
}

function copyPropertiesFromObject(objectData, product, slotName, systemDefinition, systemProducts, otherObject) {
  const object = objectData;
  // - Add old required optional accessories -
  const otherOptions = otherObject.options;
  let otherOptionalAccessoryInstance = null;
  let otherOptionalAccessoryInstances = null;
  if (otherOptions) {
    // Find first optional accessory instance
    otherOptionalAccessoryInstance = otherOptions.find((item) => {
      return isOptionalAccessoryInstance(item, product, slotName, systemDefinition, systemProducts);
    });
  }
  if (otherOptionalAccessoryInstance) {
    // Collect all optional accessory instances of the same set
    const otherOptionalAccessorySet = otherOptionalAccessoryInstance.set;
    otherOptionalAccessoryInstances = otherOptions.filter((item) => {
      return item.set === otherOptionalAccessorySet;
    });
  }
  // Add previous optional accessories to the current objects's options array
  if (otherOptionalAccessoryInstances && otherOptionalAccessoryInstances.length > 0) {
    // Remove other optional accessory instances
    const curOptions = object.options;
    for (let i = curOptions.length - 1; i >= 0; i -= 1) {
      if (isOptionalAccessoryInstance(curOptions[i], product, slotName, systemDefinition, systemProducts)) {
        curOptions.splice(i, 1);
      }
    }
    otherOptionalAccessoryInstances.forEach((item) => {
      defAddAccessoryInstanceToArray(slotName, item, curOptions, systemDefinition, systemProducts);
    });
  }

  if (product) {
    const productProperties = product.properties;
    const previousProperty = otherObject.property;
    if (previousProperty === null || (productProperties && productProperties.includes(previousProperty))) {
      object.property = previousProperty;
    }
  }
}

export function assignPropertiesFromObject(slotName, systemDefinition, systemProducts, otherObject) {
  if (!otherObject) {
    return;
  }

  const object = getObjectAtSystemDefinitionSlot(slotName, systemDefinition, systemProducts);
  const product = defGetProductInfoBySlotName(slotName, systemDefinition, systemProducts);

  copyPropertiesFromObject(object, product, slotName, systemDefinition, systemProducts, otherObject);
}

export function instantiateOptionalAccessory(accessory, setId) {
  if (!accessory) {
    return null;
  }
  return {
    id: accessory.id,
    set: setId,
    // TODO: moet amount hier nog bij?
    type: accessory.model_type,
  };
}

export function instantiate(product, type, systemDefinition, systemProducts, basedOnObject) {
  const { model_type, amount, id } = product;

  const accessories = defGetSystemObjectAccessories(type, systemDefinition, systemProducts, product, false);
  const options = [];


  addRequiredAccessories(options, accessories, type, systemDefinition, systemProducts);
  // Add required optional accessories
  const optionalAccessories = getAccessoriesOfProductData(product, AccessoryCategory.ACCESSORY_COLLECTIONS);
  if (optionalAccessories) {
    const firstSet = optionalAccessories[0];
    if (firstSet) {
      const setId = getIdOfAccessorySet(firstSet);
      const { set } = firstSet;
      if (set) {
        set.forEach((item) => {
          if (item && item.required) {
            options.push(instantiateOptionalAccessory(item, setId));
          }
        });
      }
    }
  }

  const result = {
    amount,
    id,
    options,
    type: model_type,
    property: null,
    exclude_price: false,
  };

  if (basedOnObject) {
    copyPropertiesFromObject(result, product, type, systemDefinition, systemProducts, basedOnObject);
  }
  return result;
}

export function getProductInfoByData(data, systemDefinition, systemProducts, type) {
  if (!data) {
    return null;
  }
  const { id } = data;
  const idString = `${id}`;
  const set = getSystemProjectSet(type, systemProducts);
  if (!set) {
    return null;
  }
  for (let i = 0, length = set.length; i < length; i += 1) {
    const product = set[i];
    if (product) {
      const productId = `${product.id}`;
      if (productId === idString) {
        return product;
      }
    }
  }
  return null;
}

export function getProductInfoBySlotName(slotName, systemDefinition, systemProducts) {
  if (!slotName || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getProductInfoBySlotName - invalid params', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  return getProductInfoByData(data, systemDefinition, systemProducts, slotName);
}

export function getProductDataFromSystemDefinitionSlot(slotName, systemDefinition, systemProducts) {
  const { sets } = systemProducts.attributes;

  let array = null;
  array = sets[slotName];
  if (!array) {
    if (slotName === 'boiler') {
      array = sets.solar_boiler;
    }
  }
  if (!array) {
    return null;
  }

  const { slots } = systemDefinition.attributes;

  const data = slots[slotName];
  const dataId = `${data.id}`;

  const num = array.length;
  for (let i = 0; i < num; i += 1) {
    const item = array[i];
    const itemId = `${item.id}`;
    if (itemId === dataId) {
      return item;
    }
  }
  return null;
}

export function canAddAnyProductToSystemDefinition(type, systemDefinition, systemProducts) { // eslint-disable-line
  const current = getObjectAtSystemDefinitionSlot(type, systemDefinition);
  return !current;
}

export function canAddProductToSystemDefinition(type, systemDefinition, systemProducts, product) { // eslint-disable-line
  const current = getObjectAtSystemDefinitionSlot(type, systemDefinition);
  if (current) {
    return false;
  }
  const { sets } = systemProducts.attributes;
  let array = sets[type];
  if (!array) {
    if (type === 'boiler') {
      array = sets.solar_boiler;
    }
  }
  if (!array) {
    return false;
  }
  const num = array.length;
  return num > 0;
}

export function canReplaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, productInfo) { // eslint-disable-line
  const current = systemDefinition.attributes.slots[slotName];
  const { sets } = systemProducts.attributes;
  let array = sets[slotName];
  if (!array) {
    if (slotName === 'boiler') {
      array = sets.solar_boiler;
    }
  }
  if (!array) {
    return false;
  }
  const currentId = `${current.id}`;
  const num = array.length;
  if (!num) {
    return false;
  }
  for (let i = 0; i < num; i += 1) {
    const item = array[i];
    const itemId = `${item.id}`;
    if (currentId !== itemId) {
      return true;
    }
  }
  return false;
}

export function canRemoveProductFromSystemDefinition(slotName, systemDefinition, systemProducts, productInfo) { // eslint-disable-line
  const prodInf = productInfo || getProductDataFromSystemDefinitionSlot(slotName, systemDefinition, systemProducts);
  if (!prodInf) {
    return true;
  }
  return !prodInf.required;
}

function setProductToSystemDefinition(type, product, systemDefinition, systemProducts, basedOnObject) { // eslint-disable-line
  const componentType = getComponentType(type);
  if (!componentType) {
    // #if DEBUG
    console.warn('Setting product failed - invalid component target slot: ', type);
    // #endif
    return;
  }
  const { slot } = componentType;
  const systemDef = systemDefinition;
  const slots = systemDef.attributes.slots || {};
  systemDef.attributes.slots = slots;
  if (!product) {
    slots[slot] = false;
    return;
  }
  slots[slot] = defInstantiate(product, type, systemDefinition, systemProducts, basedOnObject);
}

export function addProductToSystemDefinition(type, product, systemDefinition, systemProducts) { // eslint-disable-line
  setProductToSystemDefinition(type, product, systemDefinition, systemProducts);
}

export function replaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, product) { // eslint-disable-line
  const previousData = getObjectAtSystemDefinitionSlot(slotName, systemDefinition, systemProducts);
  setProductToSystemDefinition(slotName, product, systemDefinition, systemProducts, previousData);
  // defAssignPropertiesFromObject(slotName, systemDefinition, systemProducts, previousData);
}

export function removeProductFromSystemDefinition(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  const sysDef = systemDefinition;
  const { slots } = sysDef.attributes;
  if (slots[slotName]) {
    slots[slotName] = false;
    return true;
  }
  return false;
}

export function getAvailableNewProductsForSystemDefinition(type, systemDefinition, systemProducts) { // eslint-disable-line
  if (type === 'kettle' || type === 'boiler' || type === 'solar_boiler' || type === 'pump') {
    let slotName = type;
    let setName = type;
    if (slotName === 'solar_boiler') {
      slotName = 'boiler';
    }
    const { slots } = systemDefinition.attributes;
    let old = slots[slotName];
    if (!old && slotName === 'solar_boiler') {
      old = slots[slotName];
    }
    if (old) {
      return null;
    }
    const { sets } = systemProducts.attributes;
    let arr = sets[setName];
    if (!arr && setName === 'boiler') {
      setName = 'solar_boiler';
      arr = sets[setName];
    }
    return arr ? arr.concat() : null;
  }
}

export function getAvailableReplacementProductsForSystemDefinition(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  const current = systemDefinition.attributes.slots[slotName];
  const { sets } = systemProducts.attributes;
  let array = sets[slotName];
  if (!array) {
    if (slotName === 'boiler') {
      array = sets.solar_boiler;
    }
  }
  if (!array) {
    return null;
  }
  if (!current) {
    return array;
  }
  const currentId = `${current.id}`;
  return array.filter((obj) => {
    const id = `${obj.id}`;
    return id !== currentId;
  });
}

export function resetObjectAtSystemDefinitionSlot() {
}

// ////////////////////////////////////////////////////////////////
//
// Accessories & properties
//
// ////////////////////////////////////////////////////////////////
export function accessoryInArray(id, array) {
  if (!array) {
    return false;
  }
  for (let i = 0, length = array.length; i < length; i += 1) {
    const value = array[i];
    if (value && value.id === id) {
      return true;
    }
  }
  return false;
}

export function instantiateAccessory(accessory, slotName, systemDefinition, systemProducts) { // eslint-disable-line
  if (!accessory) {
    return null;
  }
  const { id } = accessory;
  let { amount } = accessory;
  if (amount === null || typeof (amount) === 'undefined') {
    amount = 1;
  }
  const type = accessory.model_type;
  return {
    id,
    amount,
    type,
  };
}

export function removeAccessories(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  if (!systemDefinition || !systemDefinition.attributes) {
    return;
  }
  const { slots } = systemDefinition.attributes;
  if (!slots) {
    return;
  }
  const object = slots[slotName];
  if (!object) {
    return;
  }
  const { options } = object;
  if (!options) {
    return;
  }
  options.length = 0;
}

function optionsContainAccessory(accessory, options) {
  if (!options) {
    return false;
  }
  let accessoryID = accessory;
  if (typeof (accessory) === 'object' && accessory !== null) {
    accessoryID = accessory.id;
  }
  if (accessoryID === null || typeof (accessoryID) === 'undefined') {
    return false;
  }

  const numOptions = options.length;
  if (!numOptions) {
    return false;
  }
  for (let i = 0; i < numOptions; i += 1) {
    const opt = options[i];
    if (opt && opt.id === accessoryID) {
      return true;
    }
  }
  return false;
}

export function addAccessoryInstanceToArray(accessoryInstance, options, systemDefinition, systemProducts, type) { // eslint-disable-line
  if (!options) {
    return;
  }
  if (optionsContainAccessory(accessoryInstance, options)) {
    // Already added
    return;
  }
  /*
  const accessoryID = accessoryInstance.id;

  const numOptions = options.length;
  for (let i = 0; i < numOptions; i += 1) {
    const opt = options[i];
    if (opt && opt.id === accessoryID) {
      // Already added
      return;
    }
  }
  */
  options.push(accessoryInstance);
}

function addAccessoryToArrayInternal(accessory, isOptional, optionalAccessorySet, options, systemDefinition, systemProducts, type) { // eslint-disable-line
  if (!options) {
    return;
  }
  if (optionsContainAccessory(accessory, options)) {
    // Already added
    return;
  }
  /*
  const accessoryID = accessory.id;

  const numOptions = options.length;
  for (let i = 0; i < numOptions; i += 1) {
    const opt = options[i];
    if (opt && opt.id === accessoryID) {
      // Already added
      return;
    }
  }
  */

  let resultAccessory = null;
  if (isOptional) {
    resultAccessory = instantiateOptionalAccessory(accessory, optionalAccessorySet);
  } else {
    resultAccessory = instantiateAccessory(accessory);
  }
  if (resultAccessory) {
    options.push(resultAccessory);
  }
}

/*
product {
  accessories: [
    {},
    {},
    ...
  ],
  // optional_accessories: [
  accessory_collections: {
    key: [
    {
      set_id: ...,
      set_entry_id: ...,
      set: [
      {},
      {},
      ...
    ]
  }
]
  }
}
*/

export function addOptionalAccessoryToArray(accessory, optionalAccessorySet, options, systemDefinition, systemProducts, type) { // eslint-disable-line
  return addAccessoryToArrayInternal(accessory, true, optionalAccessorySet, options, systemDefinition, systemProducts, type);
}

export function addAccessoryToArray(accessory, options, systemDefinition, systemProducts, type) { // eslint-disable-line
  return addAccessoryToArrayInternal(accessory, false, null, options, systemDefinition, systemProducts, type);
}

export function addOptionalAccessory(slotName, accessory, optionalAccessorySet, systemDefinition, systemProducts) { // eslint-disable-line
  if (!slotName || !accessory || !systemDefinition || !systemDefinition.attributes) {
    return;
  }
  const { slots } = systemDefinition.attributes;
  if (!slots) {
    return;
  }
  const object = slots[slotName];
  if (!object) {
    return;
  }
  const { options } = object;
  defAddOptionalAccessoryToArray(slotName, accessory, optionalAccessorySet, options, systemDefinition, systemProducts);
}

export function addAccessory(slotName, accessory, systemDefinition, systemProducts) { // eslint-disable-line
  if (!slotName || !accessory || !systemDefinition || !systemDefinition.attributes) {
    return;
  }
  const { slots } = systemDefinition.attributes;
  if (!slots) {
    return;
  }
  const object = slots[slotName];
  if (!object) {
    return;
  }
  const { options } = object;
  defAddAccessoryToArray(slotName, accessory, options, systemDefinition, systemProducts);
}

export function removeAccessory(slotName, accessory, systemDefinition, systemProducts) { // eslint-disable-line
  if (!systemDefinition || !systemDefinition.attributes) {
    return false;
  }
  const { slots } = systemDefinition.attributes;
  if (!slots) {
    return false;
  }
  const object = slots[slotName];
  if (!object) {
    return false;
  }
  const { options } = object;
  if (!options) {
    return false;
  }
  const accessoryId = `${accessory.id}`;

  let res = false;
  for (let i = options.length - 1; i >= 0; i -= 1) {
    const optionData = options[i];
    if (optionData) {
      const optionDataId = `${optionData.id}`;
      if (accessoryId === optionDataId) {
        options.splice(i, 1);
        res = true;
      }
    }
  }
  return res;
}

export function getAccessoriesArrayByProduct(product, onlyOptionalAccessories) {
  // TODO: Use getAccessoriesArrayByProduct OR getAccessoriesOfProductData
  // Two functions doing exactly the same is ridiculous
  return getAccessoriesOfProductData(product, onlyOptionalAccessories);
}

export function getSystemObjectAccessoriesByData(slotName, data, systemDefinition, systemProducts, productParam, onlyOptionalAccessories) {
  if (!data || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectAccessories - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  let product = productParam;

  if (!product) {
    if (!data) {
      return null;
    }
    const currentId = data.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  return getAccessoriesArrayByProduct(product, onlyOptionalAccessories);
}

export function getSystemObjectAccessories(slotName, systemDefinition, systemProducts, productParam, onlyOptionalAccessories) {
  if (!slotName || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectAccessories - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  let product = productParam;

  if (!product) {
    const objectDef = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
    if (!objectDef) {
      return null;
    }
    const currentId = objectDef.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  return getAccessoriesArrayByProduct(product, onlyOptionalAccessories);
}

export function getSystemObjectSelectedAccessories(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  const objectDef = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!objectDef) {
    return null;
  }
  return objectDef.options;
}

export function findProductAccessory(option, product, useSingleAccessories = true, useAccessoryCollections = true) {
  if (!product) {
    return null;
  }
  const optionType = typeof (option);
  let optionId = null;
  if (optionType === 'object' && option !== null) {
    optionId = option.id;
  } else if (optionType === 'string' || optionType === 'number') {
    optionId = option;
  }
  if (optionId === null || typeof (optionId) === 'undefined') {
    return null;
  }

  const accessories = getAccessoriesOfProductData(product, AccessoryCategory.ACCESSORIES);
  const accessoryCollections = getAccessoriesOfProductData(product, AccessoryCategory.ACCESSORY_COLLECTIONS);

  const numAccessories = useSingleAccessories && accessories ? accessories.length : 0;
  for (let i = 0; i < numAccessories; i += 1) {
    const accessory = accessories[i];
    if (accessory && (accessory.id === optionId)) {
      return {
        accessory,
        type: 'default',
      };
    }
  }


  const accessoryCollectionKeys = accessoryCollections ? Object.keys(accessoryCollections) : null;
  const numAccessoryCollectionKeys = accessoryCollectionKeys ? accessoryCollectionKeys.length : 0;
  for (let k = 0; k < numAccessoryCollectionKeys; k += 1) {
    const accessoryCollectionKey = accessoryCollectionKeys[k];
    const accessoryCollection = accessoryCollectionKey ? accessoryCollections[accessoryCollectionKey] : null;

    const numCollectionAccessories = useAccessoryCollections && accessoryCollection ? accessoryCollection.length : 0;
    for (let i = 0; i < numCollectionAccessories; i += 1) {
      const accessorySet = accessoryCollections[i];
      const accessorySetId = getIdOfAccessorySet(accessorySet);
      const accessorySetArr = accessorySet ? accessorySet.set : null;
      const setCount = accessorySetArr ? accessorySetArr.length : 0;
      for (let j = 0; j < setCount; j += 1) {
        const accessory = accessorySetArr[j];
        if (accessory && (accessory.id === optionId)) {
          return {
            accessory,
            set: accessorySetId,
            type: 'optional',
          };
        }
      }
    }
  }


  return null;
}

export function findAccessoryInfo(
  accessory,
  slotName,
  systemDefinition,
  systemProducts,
  regularAccessories = true,
  optionalAccessories = true,
) {
  const productInfo = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);

  return findProductAccessory(accessory, productInfo, regularAccessories, optionalAccessories);
}

export function setSystemObjectPropertyValue(slotName, value, systemDefinition) {
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return;
  }
  data.property = value;
}

export function getSystemObjectPropertyValue(slotName, systemDefinition) {
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return null;
  }
  return data.property;
}

export function getSystemObjectProperties(slotName, systemDefinition, systemProducts, productParam) {
  if (!slotName || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectProperties - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }

  let product = productParam;
  if (!product) {
    // * By default, get the products defined under the given slotname *

    // Get the product data from the definition slots (kettle, external_unit, boiler, ...)
    const objectDef = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
    if (!objectDef) {
      return null;
    }
    const currentId = objectDef.id;
    if (currentId === null || typeof (currentId) === 'undefined') {
      return null;
    }

    // Get product by id
    const productResult = getProductsBySlotNameAndId(slotName, currentId, systemProducts);
    product = Array.isArray(productResult) ? productResult[0] : productResult;
  }
  return product ? product.properties : null;
}

export function findOptionIndex(option, slotName, systemDefinition, systemProducts) { // eslint-disable-line
  if (!option) {
    return -1;
  }
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return -1;
  }
  const { options } = data;
  if (!options) {
    return -1;
  }
  const numOptions = options.length;
  if (!numOptions) {
    return -1;
  }
  let optionId = option;
  let type = null;
  if (option && typeof (option) === 'object') {
    optionId = option.id;
    type = option.model_type;
  }
  if (!optionId) {
    return -1;
  }
  for (let i = 0; i < numOptions; i += 1) {
    const opt = options[i];
    const optId = opt ? opt.id : null;
    const optType = opt ? opt.type : null;
    if (optId === optionId && (type ? optType === type : true)) {
      return i;
    }
  }
  return -1;
}

export function canExcludeFromQuote(slotName, systemDefinition, systemProducts) { // eslint-disable-line
  return true;
}

// /////////////////////////////////////////////////////////////////////////////
//
// Other product properties
//
// /////////////////////////////////////////////////////////////////////////////
export function getProductName(
  slotName,
  systemDefinition,
  systemProducts,
  productData,
  intl, // eslint-disable-line
) {
  let pd = productData;
  if (!productData) {
    pd = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  }
  if (!pd) {
    return null;
  }
  return pd.name;
}

export function getProductBrandName(
  slotName,
  systemDefinition,
  systemProducts,
  productData,
  intl, // eslint-disable-line
) {
  let pd = productData;
  if (!productData) {
    pd = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  }
  if (!pd) {
    return null;
  }
  return pd.brand;
}

export function getProductDescription(
  slotName,
  systemDefinition,
  systemProducts,
  productData,
  intl, // eslint-disable-line
) {
  let pd = productData;
  if (!productData) {
    pd = getProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  }
  if (!pd) {
    return null;
  }
  return pd.description;
}

export function hasProductDescription(
  slotName,
  systemDefinition,
  systemProducts,
  productData, // eslint-disable-line
) {
  return true;
}



// WEBPACK FOOTER //
// ./js/utils/project-definition/component/defaults.js